<template>
  <div v-if="parsedDate" class="article-date" :class="computedClass">
    <span class="date--month">{{ parsedDate | formatDate({ format: 'MMM' }) }}</span>
    <span class="date--day">{{ parsedDate | formatDate({ format: 'DD' }) }}</span>
    <span class="date--year">{{ parsedDate | formatDate({ format: 'YYYY' }) }}</span>
  </div>
</template>

<script>
  import isValidDate from 'date-fns/is_valid'
  import parse from 'date-fns/parse'
  import { error, warn } from '~/utils/errors'
  import { date as formatDate } from '~/utils/filters'

  export default {
    name: 'ArticleDate',
    filters: {
      formatDate,
    },
    props: {
      /**
       * Date object to output.
       * @type {Date|String}
       */
      date: {
        default: null,
        type: [Date, String],
      },
      /**
       * Controls removing padding and bottom border.
       * @type {Boolean}
       */
      plain: {
        default: false,
        type: Boolean,
      },
      /**
       * Size variant.
       * @type {string}
       */
      size: {
        default: 'regular',
        type: String,
        validate: value => ['small', 'regular'].indexOf(value) > -1,
      },
    },
    computed: {
      computedClass () {
        return {
          'article-date__plain': this.plain,
          'article-date__small': this.size === 'small',
        }
      },
      /**
       * Always return either a date object or null.
       * @return {Date|null}
       */
      parsedDate () {
        if (this.date instanceof Date) return this.date
        if (typeof this.date !== 'string') {
          warn(`The 'date' prop must be either an instance of Date or a valid date string.`)
          return null
        }

        try {
          const parsed = parse(this.date)

          if (isValidDate(parsed)) return parsed

          warn(`Couldn't parse the 'date' string property.`)
        } catch (err) {
          error(err)
        }

        return null
      },
    },
  }
</script>

<style lang="scss" scoped>
  @import "~assets/scss/frontend/_colours";
  @import "~assets/scss/frontend/_fonts";
  @import "~assets/scss/frontend/_settings";
  @import "~foundation-sites/scss/global";

  .article-date {
    border-bottom: $global-border;
    color: $colour-grey;
    font: {
      family: $font-officina;
      size: rem-calc(25);
    }
    line-height: 1;
    padding-bottom: rem-calc(20);

    > * {
      display: block;
    }

    .date--day {
      font-size: rem-calc(45);
    }
    .date--month {
      text-transform: uppercase;
    }

    //// Size variants.
    &.article-date__small {
      font-size: rem-calc(18);

      .date--day {
        font-size: rem-calc(30);
      }
    }

    //// Plain
    &.article-date__plain {
      border-bottom: none;
      padding-bottom: 0;
    }
  }
</style>
